import { api } from '@/api';
import { ActionContext } from 'vuex';
import {
  commitSetUsers,
  commitSetUser,
  commitSetTemplate,
  commitSetService,
  commitSetContracts,
  commitSetServices,
  commitSetTemplates,
  commitSetProxyErrors,
  commitSetPriorities,
  commitSetProxyError,
  commitSetSenders,
  commitSetSender,
  commitSetPriority,
  commitSetTemplateProxyResponse,
  commitSetMNOs,
  commitSetMNO,
  commitSetMNOMasks,
  commitSetCheckMNOMaskResponse,
  commitSetMNOSenders,
  commitSetTags,
  commitSetServiceBanPolicy,
  commitSetServiceBanPolicies,
} from './accessors/commit';
import {
  IMessage,
  IServiceCreate,
  IServiceUpdate,
  ITemplateCreate,
  ITemplateUpdate,
  IUserProfileCreate,
  IUserProfileUpdate,
  IProxyErrorCreate,
  IProxyErrorUpdate,
  ISenderUpdate,
  ISenderCreate,
  IPriorityUpdate,
  IProxyUserBanCreate,
  IMNOUpdate,
  IMNOCreate,
  IMNOMaskCreate,
  IMNOSenderCreate,
  IMNOSenderUpdate,
  IServiceBanPolicyCreateUpdate,
  IContract,
} from '@/interfaces';
import { State } from '../state';
import { AdminState } from './state';
import { dispatchCheckApiError, commitAddNotification, commitRemoveNotification } from '../main/accessors';
import { ICheckTemplateProxy } from '@/interfaces/utilities';
import { dispatchGetServiceBanPolicies } from '@/store/admin/accessors';

type MainContext = ActionContext<AdminState, State>;

export const actions = {
  async actionGetUsers(context: MainContext) {
    try {
      const response = await api.getUsers(context.rootState.main.token);
      if (response) {
        commitSetUsers(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionGetUser(context: MainContext, id: number) {
    try {
      const response = await api.getUser(context.rootState.main.token, id);
      if (response) {
        commitSetUser(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdateUser(context: MainContext, payload: { id: number; user: IUserProfileUpdate }) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.updateUser(context.rootState.main.token, payload.id, payload.user),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetUser(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'User successfully updated', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionCreateUser(context: MainContext, payload: IUserProfileCreate) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.createUser(context.rootState.main.token, payload),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetUser(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'User successfully created', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteUser(context: MainContext, id: number) {
    try {
      const loadingNotification = { content: 'deleting', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.deleteUser(context.rootState.main.token, id),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'User successfully deleted', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteUsers(context: MainContext, ids: number[]) {
    try {
      const loadingNotification = { content: 'deleting', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.deleteUsers(context.rootState.main.token, ids),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Users successfully deleted', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionGetContracts(context: MainContext) {
    try {
      const response = await api.getContracts(context.rootState.main.token);
      if (response) {
        commitSetContracts(context, response.data?.items ?? []);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionGetServices(context: MainContext) {
    try {
      const response = await api.getServices(context.rootState.main.token);
      if (response) {
        commitSetServices(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionGetService(context: MainContext, id: number) {
    try {
      const response = await api.getService(context.rootState.main.token, id);
      if (response) {
        commitSetService(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionCreateService(context: MainContext, payload: IServiceCreate) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.createService(context.rootState.main.token, payload),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetService(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Service successfully created', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdateService(context: MainContext, payload: { id: number; service: IServiceUpdate }) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.updateService(context.rootState.main.token, payload.id, payload.service),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetService(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Service successfully updated', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteService(context: MainContext, id: number) {
    try {
      const loadingNotification = { content: 'deleting', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.deleteService(context.rootState.main.token, id),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Service successfully deleted', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteServices(context: MainContext, ids: number[]) {
    try {
      const loadingNotification = { content: 'deleting', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.deleteServices(context.rootState.main.token, ids),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Services successfully deleted', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionGetSenders(context: MainContext) {
    try {
      const response = await api.getSenders(context.rootState.main.token);
      if (response) {
        commitSetSenders(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionGetSender(context: MainContext, id: number) {
    try {
      const response = await api.getSender(context.rootState.main.token, id);
      if (response) {
        commitSetSender(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionCreateSender(context: MainContext, payload: ISenderCreate) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.createSender(context.rootState.main.token, payload),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetSender(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Sender successfully created', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdateSender(context: MainContext, payload: { id: number; sender: ISenderUpdate }) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.updateSender(context.rootState.main.token, payload.id, payload.sender),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetSender(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Sender successfully updated', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteSender(context: MainContext, id: number) {
    try {
      const loadingNotification = { content: 'deleting', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.deleteSender(context.rootState.main.token, id),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Sender successfully deleted', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteSenders(context: MainContext, ids: number[]) {
    try {
      const loadingNotification = { content: 'deleting', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.deleteSenders(context.rootState.main.token, ids),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Senders successfully deleted', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionGetTemplates(
    context: MainContext,
    payload: {
      search: string;
      active: boolean | null;
      priorityId: number | null;
      senderId: number | null;
      serviceId: number | null;
      mnoBanId: number | null;
      tagId: number | null;
      skip: number;
      limit: number;
      orderBy: string;
      descending: boolean;
    },
  ) {
    try {
      const response = await api.getTemplates(
        context.rootState.main.token,
        payload.search,
        payload.active,
        payload.priorityId,
        payload.senderId,
        payload.serviceId,
        payload.mnoBanId,
        payload.tagId,
        payload.skip,
        payload.limit,
        payload.orderBy,
        payload.descending,
      );
      if (response) {
        commitSetTemplates(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionGetTemplate(context: MainContext, id: number) {
    try {
      const response = await api.getTemplate(context.rootState.main.token, id);
      if (response) {
        commitSetTemplate(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionCreateTemplate(context: MainContext, payload: ITemplateCreate) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.createTemplate(context.rootState.main.token, payload),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetTemplate(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Template successfully created', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdateTemplate(context: MainContext, payload: { id: number; template: ITemplateUpdate }) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.updateTemplate(context.rootState.main.token, payload.id, payload.template),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetTemplate(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Template successfully updated', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteTemplate(context: MainContext, id: number) {
    try {
      const loadingNotification = { content: 'deleting', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.deleteTemplate(context.rootState.main.token, id),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Template successfully deleted', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteTemplates(context: MainContext, ids: number[]) {
    try {
      const loadingNotification = { content: 'deleting', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.deleteTemplates(context.rootState.main.token, ids),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Templates successfully deleted', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionParseMessage(context: MainContext, payload: IMessage) {
    try {
      const loadingNotification = { content: 'Parsing message', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.parseMessage(context.rootState.main.token, payload),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionGetPriorities(context: MainContext) {
    try {
      const response = await api.getPriorities(context.rootState.main.token);
      if (response) {
        commitSetPriorities(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdatePriority(context: MainContext, payload: { id: number; priority: IPriorityUpdate }) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.updatePriority(context.rootState.main.token, payload.id, payload.priority),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetPriority(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Priority successfully updated', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionGetMNO(context: MainContext, mnoId: number) {
    try {
      const response = await api.getMNO(context.rootState.main.token, mnoId);
      if (response) {
        commitSetMNO(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionGetMNOs(context: MainContext) {
    try {
      const response = await api.getMNOs(context.rootState.main.token);
      if (response) {
        commitSetMNOs(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdateMNO(context: MainContext, payload: { id: number; mno: IMNOUpdate }) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.updateMNO(context.rootState.main.token, payload.id, payload.mno),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetMNO(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'MNO successfully updated', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionCreateMNO(context: MainContext, mno: IMNOCreate) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.createMNO(context.rootState.main.token, mno),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetMNO(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'MNO successfully created', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionCheckTemplateProxy(context: MainContext, payload: ICheckTemplateProxy) {
    try {
      const loadingNotification = { content: 'check template proxy response', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = await api.checkTemplateProxy(context.rootState.main.token, payload);
      commitSetTemplateProxyResponse(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionCheckMNOMask(context: MainContext, phone: string) {
    try {
      const loadingNotification = { content: 'checking mno mask', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = await api.checkMNOMask(context.rootState.main.token, phone);
      commitSetCheckMNOMaskResponse(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionGetMNOMasks(context: MainContext, payload: { skip: number; limit: number; mnoId: number }) {
    try {
      const loadingNotification = { content: 'loading MNO masks', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = await api.getMNOMasks(context.rootState.main.token, payload.skip, payload.limit, payload.mnoId);
      commitSetMNOMasks(context, response.data.items);
      commitRemoveNotification(context, loadingNotification);
      return response.data.total;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteMNOMasks(context: MainContext, payload: { mnoId: number; ids: number[] }) {
    try {
      const loadingNotification = { content: 'deleting MNO mask', showProgress: true };
      commitAddNotification(context, loadingNotification);
      await api.deleteMNOMasks(context.rootState.main.token, payload.mnoId, payload.ids);
      commitRemoveNotification(context, loadingNotification);
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionAddMNOMask(context: MainContext, payload: { mnoId: number; data: IMNOMaskCreate }) {
    try {
      const loadingNotification = { content: 'adding MNO mask', showProgress: true };
      commitAddNotification(context, loadingNotification);
      await api.createMNOMask(context.rootState.main.token, payload.mnoId, payload.data);
      commitRemoveNotification(context, loadingNotification);
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionGetMNOSenders(context: MainContext, mnoId: number) {
    try {
      const loadingNotification = { content: 'loading MNO senders', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = await api.getMNOSenders(context.rootState.main.token, mnoId);
      commitSetMNOSenders(context, response.data);
      commitRemoveNotification(context, loadingNotification);
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteMNOSenders(context: MainContext, payload: { mnoId: number; ids: number[] }) {
    try {
      const loadingNotification = { content: 'deleting MNO sender', showProgress: true };
      commitAddNotification(context, loadingNotification);
      await api.deleteMNOSenders(context.rootState.main.token, payload.mnoId, payload.ids);
      commitRemoveNotification(context, loadingNotification);
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteMNO(context: MainContext, payload: { id: number }) {
    try {
      const loadingNotification = { content: 'deleting MNO', showProgress: true };
      commitAddNotification(context, loadingNotification);
      await api.deleteMNO(context.rootState.main.token, payload.id);
      commitRemoveNotification(context, loadingNotification);
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionCreateMNOSender(context: MainContext, payload: { mnoId: number; data: IMNOSenderCreate }) {
    try {
      const loadingNotification = { content: 'creating MNO sender', showProgress: true };
      commitAddNotification(context, loadingNotification);
      await api.createMNOSender(context.rootState.main.token, payload.mnoId, payload.data);
      commitRemoveNotification(context, loadingNotification);
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdateMNOSender(context: MainContext, payload: { mnoId: number; id: number; data: IMNOSenderUpdate }) {
    try {
      const loadingNotification = { content: 'updating MNO sender', showProgress: true };
      commitAddNotification(context, loadingNotification);
      await api.updateMNOSender(context.rootState.main.token, payload.mnoId, payload.id, payload.data);
      commitRemoveNotification(context, loadingNotification);
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionGetProxyErrors(context: MainContext, payload: { serviceId: number | null }) {
    try {
      const response = await api.getProxyErrors(context.rootState.main.token, payload.serviceId);
      if (response) {
        commitSetProxyErrors(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionGetProxyError(context: MainContext, id: number) {
    try {
      const response = await api.getProxyError(context.rootState.main.token, id);
      if (response) {
        commitSetProxyError(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionCreateProxyError(context: MainContext, payload: IProxyErrorCreate) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.createProxyError(context.rootState.main.token, payload),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetProxyError(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Proxy error successfully created', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdateProxyError(context: MainContext, payload: { id: number; proxyError: IProxyErrorUpdate }) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.updateProxyError(context.rootState.main.token, payload.id, payload.proxyError),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetProxyError(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Proxy error successfully updated', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteProxyError(context: MainContext, id: number) {
    try {
      const loadingNotification = { content: 'deleting', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.deleteProxyError(context.rootState.main.token, id),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Proxy error successfully deleted', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteProxyErrors(context: MainContext, ids: number[]) {
    try {
      const loadingNotification = { content: 'deleting', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.deleteProxyErrors(context.rootState.main.token, ids),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Proxy errors successfully deleted', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionGetServiceBanPolicies(context: MainContext) {
    try {
      const response = await api.getServiceBanPolicies(context.rootState.main.token);
      if (response) {
        commitSetServiceBanPolicies(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionGetServiceBanPolicy(context: MainContext, id: number) {
    try {
      const response = await api.getServiceBanPolicy(context.rootState.main.token, id);
      if (response) {
        commitSetServiceBanPolicy(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionCreateServiceBanPolicy(context: MainContext, payload: IServiceBanPolicyCreateUpdate) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.createServiceBanPolicy(context.rootState.main.token, payload),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetServiceBanPolicy(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Ban policy successfully created', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdateServiceBanPolicy(
    context: MainContext,
    payload: { id: number; serviceBanPolicy: IServiceBanPolicyCreateUpdate },
  ) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.updateServiceBanPolicy(context.rootState.main.token, payload.id, payload.serviceBanPolicy),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetServiceBanPolicy(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Ban policy successfully updated', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteServiceBanPolicy(context: MainContext, id: number) {
    try {
      const loadingNotification = { content: 'deleting', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.deleteServiceBanPolicy(context.rootState.main.token, id),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Ban policy successfully deleted', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteServiceBanPolicies(context: MainContext, ids: number[]) {
    try {
      const loadingNotification = { content: 'deleting', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.deleteServiceBanPolicies(context.rootState.main.token, ids),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Ban policies successfully deleted', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionGetProxyUserBans(
    context: MainContext,
    payload: { skip: number; limit: number; orderBy: string; descending: boolean; phoneQuery: string },
  ) {
    try {
      const response = await api.getProxyUserBans(
        context.rootState.main.token,
        payload.skip,
        payload.limit,
        payload.orderBy,
        payload.descending,
        payload.phoneQuery,
      );
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionCreateProxyUserBan(context: MainContext, payload: IProxyUserBanCreate) {
    try {
      const loadingNotification = { content: 'saving', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.createProxyUserBan(context.rootState.main.token, payload),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitSetProxyError(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Proxy ban successfully created', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteProxyUserBan(context: MainContext, id: number) {
    try {
      const loadingNotification = { content: 'deleting', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.deleteProxyUserBan(context.rootState.main.token, id),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Proxy user ban successfully deleted', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteProxyUserBans(context: MainContext, ids: number[]) {
    try {
      const loadingNotification = { content: 'deleting', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.deleteProxyUserBans(context.rootState.main.token, ids),
          await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Proxy user bans successfully deleted', color: 'success' });
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionGetProxyUserBansStatistics(context: MainContext) {
    try {
      const response = await api.getProxyUserBansStatistics(context.rootState.main.token);
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionGetTags(context: MainContext) {
    try {
      const response = await api.getTags(context.rootState.main.token);
      if (response) {
        commitSetTags(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionSuggestTags(context: MainContext, text?: string) {
    try {
      const response = await api.suggestTags(context.rootState.main.token, text);
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
};
